import { adminPost } from "@/lib/kbAdminClient";
import { writeKbDevRescopeIntent } from "@/lib/kbDevRescopeIntent";
import { adviseFromChunkQuality, type ChunkQuality } from "./kbAdminAiHelpers";
import type { KbAudience, KbScope, KbStatus, PanelNote, SearchResult } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  canPublishKb: boolean;
  canManageAppScope: boolean;
  canManageManufacturerScope: boolean;
  defaultWriteScope: "org" | "manufacturer" | "app";
  onNavigateToDevKbRescope?: () => void;
  dictation: { isListening: boolean; stop: () => void; reset: () => void };
  state: any;
  values: { results: SearchResult[]; articleSlug: string; articleTitle: string; articleContent: string; articleScope: KbScope; articleStatus: KbStatus; articleAudience: KbAudience; articleDocType: string; defaultArticleTitle: string };
  resetAiSession: (options?: { keepPurpose?: boolean }) => void;
  refreshDraftCount: () => Promise<void>;
  showNote: ShowNote;
};

export function useKbEditorWorkflow(p: Params) {
  const s = p.state;

  async function loadArticle(slug: string, label: string) {
    p.showNote({ tone: "info", title: "Loading article", text: `Loading "${label}".` });
    s.setSelected(p.values.results.find((result: SearchResult) => result.slug === slug) ?? null);
    s.setArticleSlug(slug);
    const response = await adminPost<{ ok: true; article: any; chunks: any[] }>("/api/kb/admin/getArticles", { slug, includeChunks: true });
    if (!response.ok) {
      s.setLoadedArticle(null);
      p.showNote({ tone: "error", title: "Load failed", text: response.error });
      return;
    }
    const article = response.data.article;
    p.resetAiSession();
    s.setLoadedArticle(article);
    s.setArticleTitle(article.title || "");
    s.setArticleContent(article.body_markdown || "");
    s.setArticleAudience((article.audience as KbAudience) || "internal");
    s.setArticleScope((article.scope as KbScope) || "org");
    s.setArticleDocType(article.doc_type || "");
    s.setArticleTagsInput((article.tags || []).join(", "));
    s.setArticleSourceUrl(article.source_url || "");
    s.setArticleStatus((article.status as KbStatus) === "draft" ? "draft" : p.canPublishKb ? "published" : "draft");
    const lengths = (response.data.chunks || []).map((chunk: any) => String(chunk?.chunk_text || "").length);
    const total = lengths.reduce((sum: number, len: number) => sum + len, 0);
    const count = lengths.length;
    const risk: ChunkQuality["risk"] = total < 300 ? "low" : total < 800 ? "moderate" : "healthy";
    const quality: ChunkQuality = { chunkCount: count, totalChunkChars: total, avgChunkChars: count > 0 ? Math.round(total / count) : 0, minChunkChars: count > 0 ? Math.min(...lengths) : 0, maxChunkChars: count > 0 ? Math.max(...lengths) : 0, risk };
    s.setChunkQuality(quality);
    s.setChunkQualityAdvice(adviseFromChunkQuality(quality));
    p.showNote({ tone: "success", title: "Article loaded", text: `Loaded "${article.title}" with ${response.data.chunks?.length ?? 0} chunk(s).` });
  }

  async function devRescopeFromLibraryResult(result: SearchResult) {
    if (!p.onNavigateToDevKbRescope) return;
    s.setDevRescopeBusy(true);
    try {
      const response = await adminPost<{ ok: true; article: any }>("/api/kb/admin/getArticles", { slug: result.slug, includeChunks: false });
      if (!response.ok) return p.showNote({ tone: "error", title: "Rescope prep failed", text: response.error });
      const a = response.data.article;
      writeKbDevRescopeIntent({
        slug: result.slug,
        title: a.title || result.title,
        bodyMarkdown: typeof a.body_markdown === "string" ? a.body_markdown : "",
        current: { scope: String(a.scope || "org"), organization_id: typeof a.organization_id === "string" ? a.organization_id : null, visibility: typeof a.visibility === "string" ? a.visibility : null, status: String(a.status || "draft"), audience: typeof a.audience === "string" ? a.audience : undefined, doc_type: a.doc_type ?? null, version: a.version != null ? Number(a.version) : null },
      });
      p.onNavigateToDevKbRescope();
    } finally {
      s.setDevRescopeBusy(false);
    }
  }

  function devRescopeFromEditor() {
    if (!p.onNavigateToDevKbRescope || !s.loadedArticle || !p.values.articleSlug.trim()) return;
    const a = s.loadedArticle as Record<string, unknown>;
    writeKbDevRescopeIntent({
      slug: p.values.articleSlug.trim(),
      title: p.values.articleTitle || String(a.title || p.values.articleSlug),
      bodyMarkdown: p.values.articleContent,
      current: {
        scope: String(a.scope || p.values.articleScope),
        organization_id: typeof a.organization_id === "string" ? a.organization_id : null,
        visibility: typeof a.visibility === "string" ? a.visibility : null,
        status: String(a.status || p.values.articleStatus),
        audience: typeof a.audience === "string" ? a.audience : p.values.articleAudience,
        doc_type: (a.doc_type as string | null | undefined) ?? (p.values.articleDocType || null),
        version: a.version != null ? Number(a.version) : null,
      },
    });
    p.onNavigateToDevKbRescope();
  }

  function startNewArticle() {
    p.resetAiSession();
    s.setSelected(null); s.setLoadedArticle(null); s.setArticleSlug(""); s.setLastArticleId(""); s.setArticleTitle(p.values.defaultArticleTitle); s.setArticleContent("");
    s.setArticleAudience("internal"); s.setArticleScope(p.defaultWriteScope); s.setArticleDocType(""); s.setArticleTagsInput(""); s.setArticleSourceUrl("");
    s.setArticleStatus(p.canPublishKb ? "published" : "draft"); s.setChunkQuality(null); s.setChunkQualityAdvice([]);
    p.showNote({ tone: "info", title: "New article", text: "Started a fresh article draft in the editor." });
  }

  function clearWorkspace() {
    const msg = "Clear all unsaved changes in the KB workspace?\n\nThis resets the article editor, AI assist session, document split proposals, and bulk import text. Nothing already saved on the server will be deleted.";
    if (!window.confirm(msg)) return;
    if (p.dictation.isListening) p.dictation.stop();
    p.dictation.reset();
    p.resetAiSession();
    s.setAiAudienceHint(""); s.setAiScopeHint(""); s.setAiDocTypeHint(""); s.setAiHelpOpen(false); s.setSplitProposals([]); s.setSplitOpen(false); s.setSplitWorking(false); s.setSplitCreating(false); s.setBulkImportText("");
    s.setConfirmDeleteSlug(null); s.setPreserveOriginalBody(true); s.setAiUpdateTitle(true); s.setAiUpdateMetadata(true); s.setArticleMode("preview");
    s.setSelected(null); s.setLoadedArticle(null); s.setArticleSlug(""); s.setLastArticleId(""); s.setArticleTitle(p.values.defaultArticleTitle); s.setArticleContent(""); s.setArticleAudience("internal"); s.setArticleScope(p.defaultWriteScope); s.setArticleDocType(""); s.setArticleTagsInput(""); s.setArticleSourceUrl(""); s.setArticleStatus(p.canPublishKb ? "published" : "draft"); s.setChunkQuality(null); s.setChunkQualityAdvice([]);
    p.showNote({ tone: "info", title: "Workspace cleared", text: "Editor, assist, split, and import buffer were reset. Server-side articles are unchanged." });
  }

  function resetSearchFilters() {
    s.setQ(""); s.setSearchAudience(""); s.setSearchScope(""); s.setSearchDocType(""); s.setSearchStatus(""); s.setResults([]);
    p.showNote({ tone: "info", title: "Filters cleared", text: "Search filters and current result list were reset." });
  }

  async function loadStarterCompanyDocs(url: string) {
    p.showNote(null);
    try {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error(`Starter docs fetch failed (${response.status})`);
      const json = await response.json();
      s.setBulkImportText(JSON.stringify(json, null, 2));
      s.setKbMode("import");
      p.showNote({ tone: "info", title: "Starter company docs loaded", text: "The built-in company-doc starter pack is ready in Bulk Import. Review the scope and publish defaults, then run the import when ready." });
    } catch (error) {
      p.showNote({ tone: "error", title: "Starter docs unavailable", text: error instanceof Error ? error.message : String(error) });
    }
  }

  return { loadArticle, devRescopeFromLibraryResult, devRescopeFromEditor, startNewArticle, clearWorkspace, resetSearchFilters, loadStarterCompanyDocs };
}
